import * as React from 'react'
import { Command as CommandPrimitive } from 'cmdk'

import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * shadcn `Command` primitive (cmdk), brought in with
 * `npx shadcn@latest add command`. It backs the `src/antd/Select` seam's
 * shadcn half and nothing else; nothing reaches it until someone flips the
 * design toggle.
 *
 * Three edits to the CLI's output:
 *
 * 1. `CommandDialog` and `CommandInput` are DROPPED. `CommandDialog` pulls in
 *    `./dialog` — the Modal seam's own primitive — for a command-palette
 *    pattern this repo does not have, and dragging the whole Radix Dialog onto
 *    every Select's chunk to serve nothing is the PRO-130 mistake. The seam
 *    renders its own `<input>` rather than `CommandPrimitive.Input`, for the
 *    reason written at that call site (cmdk spreads caller props BEFORE its
 *    own, so it would clobber the `id` that `Form.Item`'s `<label htmlFor>`
 *    points at), so the wrapper has no consumer either.
 * 2. NO `lucide-react` IMPORT. The CLI writes one for `CommandInput`'s search
 *    glyph; outside `src/icons/antd-compat` a raw lucide import renders lucide
 *    with the design toggle OFF (AGENTS.md), and
 *    `src/antd/__tests__/seam-adoption.evidence.test.tsx` fails on it. Both
 *    the input and its glyph are gone, so nothing needed re-pointing.
 * 3. No `'use client'`. This is a Vite SPA; the directive is Next.js-only and
 *    no other file in `src/components/ui` carries it.
 * ------------------------------------------------------------------------- */

// `forwardRef`, not a bare function: the Select seam takes a ref to this root
// and uses it to answer two questions Radix asks it — is a pointer-down event
// inside the control (so the popup must NOT dismiss), and which element is the
// popup's default container. A wrapper that swallows the ref makes the popup
// close on the very click that opened it.
const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(function Command({ className, ...props }, ref) {
  return (
    <CommandPrimitive
      ref={ref}
      data-slot="command"
      className={cn(
        'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
        className
      )}
      {...props}
    />
  )
})

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        'max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto',
        className
      )}
      {...props}
    />
  )
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  )
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
        className
      )}
      {...props}
    />
  )
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn('bg-border -mx-1 h-px', className)}
      {...props}
    />
  )
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  )
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        'text-muted-foreground ml-auto text-xs tracking-widest',
        className
      )}
      {...props}
    />
  )
}

export {
  Command,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
